import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { supabase } from "@/integrations/supabase/client";
import { hasShownGymNotification, markGymNotificationShown } from "./sessionStorage";

/**
 * Hook that uses centralized auth context and adds profile-specific logic
 */
export const useAuthState = () => {
  const auth = useAuth();
  const { info, error: showError } = useNotifications();
  const hasCheckedProfile = useRef(false);

  // Profile check logic - only once per session
  useEffect(() => {
    // Skip if no user, not authenticated, or already checked
    if (!auth.session?.user || !auth.isAuthenticated || hasCheckedProfile.current) {
      return;
    }

    hasCheckedProfile.current = true;

    const checkProfile = async () => {
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('role, banned, gym_id')
          .eq('id', auth.session?.user?.id)
          .single();
          
        if (error) throw error;

        if (profile?.banned) {
          console.log("🚫 User is banned");
          await auth.signOut();
          showError("Tu cuenta ha sido suspendida. Contacta al administrador.");
          return;
        }

        if (!profile?.gym_id && profile?.role !== 'admin' && !hasShownGymNotification()) {
          markGymNotificationShown();
          info("Bienvenido", {
            description: "Por favor, espera a que un administrador te asigne a un gimnasio."
          });
        }
      } catch (error) {
        console.error("❌ Error fetching user profile:", error);
      }
    };

    checkProfile();
  }, [auth.session?.user?.id, auth.isAuthenticated, auth.signOut, info, showError]);

  // Reset check flag when user signs out
  useEffect(() => {
    if (!auth.session) {
      hasCheckedProfile.current = false;
    }
  }, [auth.session]);

  return auth;
};
